import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { routeMap, SUPPORTED_LANGS } from '../../routes/routeMap';

const SITE_NAME = 'FinMaple';
const SITE_URL = 'https://www.finmaple.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const OG_LOCALE_MAP = {
  es: 'es_CA',
  en: 'en_CA',
  fr: 'fr_CA',
};

const SEOHead = ({
  title,
  description,
  seoKey,
  canonical,
  schema,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  routeKey,
}) => {
  const { t, i18n } = useTranslation('seo');
  const lng = i18n.language;

  // Resolve title: seoKey i18n → prop fallback → site default
  const resolvedTitle = seoKey
    ? (t(`pages.${seoKey}.title`, { defaultValue: '' }) || title)
    : title;
  const resolvedDescription = seoKey
    ? (t(`pages.${seoKey}.description`, { defaultValue: '' }) || description)
    : description;

  const fullTitle = resolvedTitle
    ? `${resolvedTitle} | ${SITE_NAME}`
    : t('defaultTitle', { defaultValue: `${SITE_NAME} | Finanzas inteligentes para latinos en Canad\u00e1` });

  // Build canonical URL with language prefix
  const canonicalPath = canonical || '';
  const canonicalUrl = `${SITE_URL}/${lng}${canonicalPath}`;

  // Build hreflang alternate links if we have a routeKey
  const alternateLinks = [];
  if (routeKey && routeMap[routeKey]) {
    const paths = routeMap[routeKey];
    SUPPORTED_LANGS.forEach((lang) => {
      if (paths[lang] !== undefined) {
        alternateLinks.push({
          hreflang: `${lang}-CA`,
          href: `${SITE_URL}/${lang}${paths[lang]}`,
        });
      }
    });
    // x-default points to Spanish (default language)
    alternateLinks.push({
      hreflang: 'x-default',
      href: `${SITE_URL}/es${paths.es}`,
    });
  }

  return (
    <Helmet>
      <html lang={lng} />
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Hreflang alternate links */}
      {alternateLinks.map((alt) => (
        <link
          key={alt.hreflang}
          rel="alternate"
          hrefLang={alt.hreflang}
          href={alt.href}
        />
      ))}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={OG_LOCALE_MAP[lng] || 'es_CA'} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Schema JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
